import { useEffect, useState, useMemo } from 'react';
import type { AxiosError } from 'axios';
import { apiConfig, axiosInstance } from '@/api/client';
import { HealthApi, type HealthCheck } from '@/api/generated';

interface HealthData {
  frontend: { status: string };
  backend: HealthCheck | { error: string };
}

export function useHealthCheck() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const healthApi = useMemo(() => new HealthApi(apiConfig, '', axiosInstance), []);

  useEffect(() => {
    async function fetchHealth() {
      try {
        // Fetch frontend health (includes backend in dev, frontend-only in prod)
        const frontendRes = await fetch('/health');
        if (!frontendRes.ok) {
          throw new Error(`Frontend health check failed: ${frontendRes.status}`);
        }
        const frontendData = await frontendRes.json();

        // Check if backend status is included (Vite dev server)
        // If not (nginx prod), fetch backend health separately
        let backendData: HealthCheck | { error: string };
        if (
          frontendData.backend &&
          !('error' in frontendData.backend || frontendData.backend.status === 'unknown')
        ) {
          backendData = frontendData.backend;
        } else {
          try {
            const backendRes = await healthApi.healthRetrieve();
            backendData = backendRes.data;
          } catch (err) {
            const axiosErr = err as AxiosError;
            backendData = { error: axiosErr.message || 'Backend health check failed' };
          }
        }

        setData({
          frontend: frontendData.frontend,
          backend: backendData,
        });
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Health check failed');
        setLoading(false);
      }
    }

    fetchHealth();
  }, [healthApi]);

  return { data, loading, error };
}
